import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send, MessageSquare, Clock } from 'lucide-react';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';

const Contact = () => {
    const [isVisible, setIsVisible] = useState({});
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
                    }
                });
            },
            { threshold: 0.1 }
        );

        const elements = document.querySelectorAll('.animate-on-scroll');
        elements.forEach((el) => observer.observe(el));

        return () => observer.disconnect();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
};

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const { name, email, message, } = formData;
        const subject = encodeURIComponent(`New Contact Form ${name}`);
        const body = encodeURIComponent(`Name: ${name}\nEmail: ${email}\nMessage: ${message}`);

        const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&to=sales.nuranienterprise@gmail.com&su=${subject}&body=${body}`;
        window.open(gmailLink, "_blank");

        // ✅ Clear form after submit
        setFormData({ name: '', email: '', subject: '', message: '' });
        setErrors({});
    };

    const contactInfo = [
        {
            icon: <Phone className="w-6 h-6" />,
            title: "Phone",
            details: ["+91 63520 68269", "+91 98245 66098"],
            color: "bg-green-100 text-green-600"
        },
        {
            icon: <Mail className="w-6 h-6" />,
            title: "Email",
            details: ["sales.nuranienterprise@gmail.com", "sabirkadivar31@gmail.com"],
            color: "bg-teal-100 text-teal-600"
        },
        {
            icon: <MapPin className="w-6 h-6" />,
            title: "Office",
            details: ["Royal Complex, Shop no - 2,", "Wankaner, Gujarat - 363621"],
            color: "bg-emerald-100 text-emerald-600"
        },
        {
            icon: <Clock className="w-6 h-6" />,
            title: "Working Hours",
            details: ["Mon - Sat: 9:00 AM - 6:00 PM", "Sunday: Closed"],
            color: "bg-lime-100 text-lime-600"
        }
    ];

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-gray-50">
                {/* Hero Section */}
                <section className="relative bg-gradient-to-br from-green-900 via-emerald-900 to-teal-900 text-white py-24 overflow-hidden">
                    <div className="absolute inset-0 bg-black opacity-30"></div>
                    <div className="absolute inset-0">
                        <div className="absolute top-20 left-10 w-96 h-96 bg-green-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
                        <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
                    </div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in-down">
                            Get in <span className="text-green-300">Touch</span>
                        </h1>
                        <p className="text-xl text-green-100 max-w-2xl mx-auto animate-fade-in-up">
                            Have questions or need a quote? We're here to help you navigate the world of international trade.
                        </p>
                    </div>
                </section>

                <section className="py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid lg:grid-cols-3 gap-12">
                            {/* Contact Info Cards */}
                            <div className="lg:col-span-1 space-y-6">
                                {contactInfo.map((info, index) => (
                                    <div
                                        key={index}
                                        className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300"
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className={`p-3 rounded-xl ${info.color}`}>
                                                {info.icon}
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-gray-900 mb-2">{info.title}</h3>
                                                {info.details.map((detail, idx) => (
                                                    <p key={idx} className="text-gray-600 mb-1">{detail}</p>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Contact Form */}
                            <div className="lg:col-span-2">
                                <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
                                    <div className="p-8 md:p-12">
                                        <div className="flex items-center gap-3 mb-8">
                                            <MessageSquare className="w-8 h-8 text-green-600" />
                                            <h2 className="text-3xl font-bold text-gray-900">Send us a Message</h2>
                                        </div>

                                        <form onSubmit={handleSubmit} className="space-y-6">
                                            <div className="grid md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all bg-gray-50 focus:bg-white"
                                                        placeholder="John Doe"
                                                        required
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all bg-gray-50 focus:bg-white"
                                                        placeholder="john@example.com"
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                                                <input
                                                    type="text"
                                                    name="subject"
                                                    value={formData.subject}
                                                    onChange={handleChange}
                                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all bg-gray-50 focus:bg-white"
                                                    placeholder="Inquiry about..."
                                                    required
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                                                <textarea
                                                    name="message"
                                                    value={formData.message}
                                                    onChange={handleChange}
                                                    rows="5"
                                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all bg-gray-50 focus:bg-white resize-none"
                                                    placeholder="How can we help you?"
                                                    required
                                                ></textarea>
                                            </div>

                                            <button
                                                type="submit"
                                                className="w-full bg-gradient-to-r from-green-600 to-emerald-700 text-white font-bold py-4 rounded-xl hover:shadow-lg hover:shadow-green-500/30 transform hover:translate-y-[-2px] transition-all duration-300 flex items-center justify-center gap-2"
                                            >
                                                Send Message
                                                <Send className="w-5 h-5" />
                                            </button>
                                        </form>
                                    </div>
                                    <div className="bg-gray-50 p-6 text-center text-sm text-gray-500 border-t border-gray-100">
                                        We respect your privacy. Your information is safe with us.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Map Section */}
                <section className="h-96 w-full bg-gray-200 relative grayscale hover:grayscale-0 transition-all duration-700">
                    <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d241317.11609823277!2d72.74109995709657!3d19.08219783958221!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c6306644edc1%3A0x5da4ed8f8d648c69!2sMumbai%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1625902875932!5m2!1sen!2sin"
                        title="Google Maps"
                        className="w-full h-full border-0"
                        allowFullScreen=""
                        loading="lazy"
                    ></iframe>
                </section>

                <style jsx>{`
                    @keyframes fade-in-down {
                        from { opacity: 0; transform: translateY(-20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes fade-in-up {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes blob {
                        0%, 100% { transform: translate(0, 0) scale(1); }
                        33% { transform: translate(30px, -50px) scale(1.1); }
                        66% { transform: translate(-20px, 20px) scale(0.9); }
                    }
                    .animate-fade-in-down { animation: fade-in-down 1s ease-out; }
                    .animate-fade-in-up { animation: fade-in-up 1s ease-out 0.3s both; }
                    .animate-blob { animation: blob 7s infinite; }
                    .animation-delay-2000 { animation-delay: 2s; }
                `}</style>
            </div>
            <Footer />
        </>
    );
};

export default Contact;
